import React from 'react';
import PropTypes from 'prop-types';
import './CatalogCard.css';

function CatalogCardIcon({ icon }) {
  if (!icon) return null;
  if (typeof icon === 'string') {
    const isUrl = icon.startsWith('http') || icon.startsWith('/') || icon.includes('.');
    return (
      <div className="cc-icon">
        {isUrl
          ? <img src={icon} alt="" />
          : <span>{icon}</span>
        }
      </div>
    );
  }
  return <div className="cc-icon">{icon}</div>;
}

function CatalogCard({
  id,
  icon,
  title,
  description,
  badges,
  tags,
  footer,
  actions,
  selected,
  enabled,
  viewMode,
  onClick,
  selectable,
  onSelect,
  selectedBorderColor,
}) {
  const isDisabled = enabled === false;
  const isList = viewMode === 'list';
  const isSelected = !!selected;

  const classNames = [
    'cc-card',
    isList ? 'cc-card--list' : '',
    isSelected ? 'cc-card--selected' : '',
    isDisabled ? 'cc-card--disabled' : '',
    selectable ? 'cc-card--selectable' : '',
  ].filter(Boolean).join(' ');

  const selectableStyle = selectable && isSelected && selectedBorderColor
    ? { borderColor: selectedBorderColor, backgroundColor: selectedBorderColor + '11' }
    : undefined;

  const handleCardClick = () => {
    if (isDisabled) return;
    if (selectable && onSelect) {
      onSelect(id);
    } else if (onClick) {
      onClick();
    }
  };

  const hasBadges = badges && badges.length > 0;
  const hasTags = tags && tags.length > 0;
  const hasFooter = footer && (footer.left || footer.right);
  const hasActions = actions && actions.length > 0;
  const showFooter = hasFooter || hasActions;

  const isInteractive = selectable || !!onClick;

  return (
    <div
      className={classNames}
      style={selectableStyle}
      onClick={handleCardClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } } : undefined}
    >
      <div className="cc-card-header">
        <div className="cc-icon-wrap">
          {isDisabled && icon && (
            <div className="cc-icon-disabled-overlay">🔒</div>
          )}
          <CatalogCardIcon icon={icon} />
        </div>
        <div className="cc-card-header-right">
          {selectable && isSelected && (
            <span className="cc-select-check" aria-hidden="true">✓</span>
          )}
          {hasBadges && (
            <div className="cc-badges">
              {badges.map((badge, i) => (
                <span
                  key={i}
                  className="cc-badge"
                  style={badge.color ? {
                    background: badge.color + '22',
                    color: badge.color,
                  } : undefined}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="cc-card-body">
        <div className="cc-title" title={title}>{title}</div>
        {description && <div className="cc-desc">{description}</div>}
        {hasTags && (
          <div className="cc-tags">
            {tags.map((tag, i) => (
              <span key={i} className="cc-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {showFooter && (
        <div className="cc-card-footer">
          <div className="cc-footer-text">
            {hasFooter && (
              <>
                {footer.left && <span className="cc-footer-left">{footer.left}</span>}
                {footer.right && <span className="cc-footer-right">{footer.right}</span>}
              </>
            )}
          </div>
          {hasActions && (
            <div className="cc-actions">
              {actions.map((action, i) => (
                <button
                  key={i}
                  className={`cc-action-btn cc-action-btn--${action.variant || 'secondary'}`}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (action.onClick) action.onClick(e);
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CatalogCard.propTypes = {
  id:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon:        PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title:       PropTypes.string.isRequired,
  description: PropTypes.string,
  badges:      PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, color: PropTypes.string })),
  tags:        PropTypes.arrayOf(PropTypes.string),
  footer:      PropTypes.shape({ left: PropTypes.string, right: PropTypes.string }),
  actions:     PropTypes.arrayOf(PropTypes.shape({
    label:    PropTypes.string,
    variant:  PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost']),
    onClick:  PropTypes.func,
    disabled: PropTypes.bool,
  })),
  selected:            PropTypes.bool,
  enabled:             PropTypes.bool,
  viewMode:            PropTypes.oneOf(['grid', 'list']),
  onClick:             PropTypes.func,
  selectable:          PropTypes.bool,
  onSelect:            PropTypes.func,
  selectedBorderColor: PropTypes.string,
};

CatalogCard.defaultProps = {
  viewMode:            'grid',
  selectable:          false,
  selected:            false,
  selectedBorderColor: 'var(--color-primary, #1565c0)',
};

export default CatalogCard;
